import { FocusEventHandler, useCallback, useRef } from 'react';
import { autoPlacement, autoUpdate, offset, useClick, useFloating, useInteractions } from '@floating-ui/react';
import { useResizeObserver } from 'usehooks-ts';

// Matches the option row's `gap-2` between the label and the trailing check icon. The
// gap only exists between flex siblings, so it can't be measured off the icon in
// isolation; the icon width itself is measured (see `checkIconRef` below).
const OPTION_ROW_GAP = 8;

export function useDropdownFloating() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const checkIconRef = useRef<HTMLSpanElement>(null);

  // Setup floating UI
  const { refs, floatingStyles, context } = useFloating({
    strategy: 'fixed',
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      autoPlacement({
        allowedPlacements: ['top', 'bottom'],
      }),
    ],
  });

  // Setup interactions
  const click = useClick(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([click]);

  // Setup resize observer for width matching
  const { width = 0 } = useResizeObserver({
    ref: wrapperRef as React.RefObject<HTMLDivElement>,
    box: 'border-box',
  });

  // Options reserve a trailing check icon that the button has no equivalent of. Measure
  // the rendered icon so the panel can be widened by exactly that column (icon + row
  // gap), keeping option labels aligned with the button text instead of truncating them.
  const { width: checkIconWidth = 0 } = useResizeObserver({
    ref: checkIconRef as React.RefObject<HTMLSpanElement>,
    box: 'border-box',
  });

  // Create blur handler function that returns a proper event handler
  const createBlurHandler = useCallback(
    (onBlur?: () => void): FocusEventHandler<HTMLButtonElement> => {
      return (e) => {
        // Don't call onBlur if the focus is moving to the floating element
        if (e.relatedTarget && e.relatedTarget === refs.floating.current) {
          return;
        }
        onBlur?.();
      };
    },
    [refs.floating],
  );

  return {
    wrapperRef,
    checkIconRef,
    checkIconColumnWidth: checkIconWidth > 0 ? checkIconWidth + OPTION_ROW_GAP : 0,
    floatingRefs: refs,
    floatingStyles,
    floatingWidth: width,
    getReferenceProps,
    getFloatingProps,
    createBlurHandler,
  };
}
